import { Injectable } from '@angular/core';
import type { PublicKeyCredentialCreationOptionsJSON } from '@core/api/model/publicKeyCredentialCreationOptionsJSON';
import type { PublicKeyCredentialRequestOptionsJSON } from '@core/api/model/publicKeyCredentialRequestOptionsJSON';
import type { PublicKeyCredentialDescriptorJSON } from '@core/api/model/publicKeyCredentialDescriptorJSON';
import type { RegistrationResponseJSON } from '@core/api/model/registrationResponseJSON';
import type { AuthenticationResponseJSON } from '@core/api/model/authenticationResponseJSON';
import type { AuthenticatorAttestationResponseJSON } from '@core/api/model/authenticatorAttestationResponseJSON';
import type { AuthenticatorAssertionResponseJSON } from '@core/api/model/authenticatorAssertionResponseJSON';
import { PublicKeyCredentialType } from '@core/api/model/publicKeyCredentialType';
import type { AuthenticatorTransportFuture } from '@core/api/model/authenticatorTransportFuture';
import { arrayBufferToBase64URL, base64URLToArrayBuffer } from './webauthn-utils';

/**
 * Wraps the browser WebAuthn API and maps credentials to OpenAPI JSON types.
 */
@Injectable({
  providedIn: 'root',
})
export class WebAuthnService {
  /**
   * @returns True when passkeys (WebAuthn) can be used in this browser.
   */
  isPasskeySupported(): boolean {
    return (
      typeof globalThis.PublicKeyCredential !== 'undefined' &&
      globalThis.navigator?.credentials != null &&
      typeof globalThis.navigator.credentials.create === 'function' &&
      typeof globalThis.navigator.credentials.get === 'function'
    );
  }

  /**
   * Runs `navigator.credentials.create` using server-provided JSON options.
   *
   * @param optionsJson - Creation options from the API.
   * @returns Registration payload for verify endpoint.
   */
  async createRegistrationResponse(
    optionsJson: PublicKeyCredentialCreationOptionsJSON,
  ): Promise<RegistrationResponseJSON> {
    const publicKey = this.toPublicKeyCredentialCreationOptions(optionsJson);
    const credential = (await globalThis.navigator.credentials.create({
      publicKey,
    })) as PublicKeyCredential | null;

    if (credential === null) {
      throw new Error('Passkey registration was cancelled or failed.');
    }

    return this.publicKeyCredentialToRegistrationJSON(credential);
  }

  /**
   * Runs `navigator.credentials.get` using server-provided JSON options.
   *
   * @param optionsJson - Request options from the API.
   * @returns Authentication payload for verify endpoint.
   */
  async createAuthenticationResponse(
    optionsJson: PublicKeyCredentialRequestOptionsJSON,
  ): Promise<AuthenticationResponseJSON> {
    const publicKey = this.toPublicKeyCredentialRequestOptions(optionsJson);
    const credential = (await globalThis.navigator.credentials.get({
      publicKey,
    })) as PublicKeyCredential | null;

    if (credential === null) {
      throw new Error('Passkey authentication was cancelled or failed.');
    }

    return this.publicKeyCredentialToAuthenticationJSON(credential);
  }

  private mapDescriptor(c: PublicKeyCredentialDescriptorJSON): PublicKeyCredentialDescriptor {
    const base: PublicKeyCredentialDescriptor = {
      type: c.type,
      id: base64URLToArrayBuffer(c.id),
    };
    if (c.transports !== undefined && c.transports.length > 0) {
      return {
        ...base,
        transports: c.transports as AuthenticatorTransport[],
      };
    }
    return base;
  }

  private toPublicKeyCredentialCreationOptions(
    json: PublicKeyCredentialCreationOptionsJSON,
  ): PublicKeyCredentialCreationOptions {
    const excludeCredentials: PublicKeyCredentialDescriptor[] | undefined =
      json.excludeCredentials?.map((c: PublicKeyCredentialDescriptorJSON) => this.mapDescriptor(c));

    return {
      rp: json.rp,
      user: {
        id: base64URLToArrayBuffer(json.user.id),
        name: json.user.name,
        displayName: json.user.displayName,
      },
      challenge: base64URLToArrayBuffer(json.challenge),
      pubKeyCredParams: json.pubKeyCredParams,
      timeout: json.timeout,
      excludeCredentials,
      authenticatorSelection: json.authenticatorSelection,
      attestation: json.attestation,
      attestationFormats: json.attestationFormats,
      extensions: json.extensions as AuthenticationExtensionsClientInputs | undefined,
    } as unknown as PublicKeyCredentialCreationOptions;
  }

  private toPublicKeyCredentialRequestOptions(
    json: PublicKeyCredentialRequestOptionsJSON,
  ): PublicKeyCredentialRequestOptions {
    const allowCredentials: PublicKeyCredentialDescriptor[] | undefined =
      json.allowCredentials?.map((c: PublicKeyCredentialDescriptorJSON) => this.mapDescriptor(c));

    return {
      challenge: base64URLToArrayBuffer(json.challenge),
      timeout: json.timeout,
      rpId: json.rpId,
      allowCredentials,
      userVerification: json.userVerification,
      extensions: json.extensions as AuthenticationExtensionsClientInputs | undefined,
    } as unknown as PublicKeyCredentialRequestOptions;
  }

  private publicKeyCredentialToRegistrationJSON(
    credential: PublicKeyCredential,
  ): RegistrationResponseJSON {
    const response = credential.response as AuthenticatorAttestationResponse;
    const transportsUnknown = response.getTransports?.() as string[] | undefined;
    const transports: AuthenticatorTransportFuture[] | undefined =
      transportsUnknown !== undefined && transportsUnknown.length > 0
        ? (transportsUnknown as AuthenticatorTransportFuture[])
        : undefined;

    const responseJson: AuthenticatorAttestationResponseJSON = {
      clientDataJSON: arrayBufferToBase64URL(response.clientDataJSON),
      attestationObject: arrayBufferToBase64URL(response.attestationObject),
    };
    if (transports !== undefined && transports.length > 0) {
      responseJson.transports = transports;
    }

    const registrationAttachment = credential.authenticatorAttachment;
    return {
      id: credential.id,
      rawId: arrayBufferToBase64URL(credential.rawId),
      response: responseJson,
      clientExtensionResults: credential.getClientExtensionResults(),
      type: PublicKeyCredentialType.PublicKey,
      ...(registrationAttachment !== undefined
        ? {
            authenticatorAttachment:
              registrationAttachment as unknown as RegistrationResponseJSON['authenticatorAttachment'],
          }
        : {}),
    } as RegistrationResponseJSON;
  }

  private publicKeyCredentialToAuthenticationJSON(
    credential: PublicKeyCredential,
  ): AuthenticationResponseJSON {
    const response = credential.response as AuthenticatorAssertionResponse;
    const rawUserHandle = response.userHandle;
    const userHandle =
      rawUserHandle != null && rawUserHandle.byteLength > 0
        ? arrayBufferToBase64URL(rawUserHandle)
        : undefined;

    const responseJson: AuthenticatorAssertionResponseJSON = {
      clientDataJSON: arrayBufferToBase64URL(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64URL(response.authenticatorData),
      signature: arrayBufferToBase64URL(response.signature),
    };
    if (userHandle !== undefined) {
      responseJson.userHandle = userHandle;
    }

    const authenticationAttachment = credential.authenticatorAttachment;
    return {
      id: credential.id,
      rawId: arrayBufferToBase64URL(credential.rawId),
      response: responseJson,
      clientExtensionResults: credential.getClientExtensionResults(),
      type: PublicKeyCredentialType.PublicKey,
      ...(authenticationAttachment !== undefined
        ? {
            authenticatorAttachment:
              authenticationAttachment as unknown as AuthenticationResponseJSON['authenticatorAttachment'],
          }
        : {}),
    } as AuthenticationResponseJSON;
  }
}
